import { create } from "zustand";
import { documentService, type Document } from "@/services/documentService";
import type { DocumentIndexingStatus } from "@/services/documentService";

/** Stable empty reference for Zustand selectors (React 19 useSyncExternalStore). */
export const EMPTY_PROJECT_DOCUMENTS: Document[] = [];

export interface ProjectDocumentSummary {
  id: string;
  documentName: string;
  documentSize: number;
  documentType: string;
  indexingStatus: DocumentIndexingStatus;
}

interface ProjectDocumentState {
  documents: Document[];
  loading: boolean;
  error: string | null;
}

interface ProjectDocumentStore {
  byProjectId: Record<string, ProjectDocumentState | undefined>;
  inflightByProjectId: Record<string, Promise<Document[]> | undefined>;
  recentRealtimeUpsertIdsByProject: Record<string, Set<string> | undefined>;
  deletedDocumentIdsByProject: Record<string, Set<string> | undefined>;
  fetchProjectDocuments: (projectId: string) => Promise<Document[]>;
  patchDocumentIndexing: (
    projectId: string,
    documentId: string,
    indexingStatus: DocumentIndexingStatus,
    indexingError?: string | null
  ) => boolean;
  upsertProjectDocument: (projectId: string, document: Document) => void;
  upsertProjectDocumentSummary: (
    projectId: string,
    document: ProjectDocumentSummary
  ) => void;
  removeProjectDocument: (projectId: string, documentId: string) => void;
  invalidateProject: (projectId: string) => void;
  invalidateAllLoadedProjects: () => Promise<void>;
  getDocuments: (projectId: string | null) => Document[];
  getLoading: (projectId: string | null) => boolean;
  getError: (projectId: string | null) => string | null;
}

export function selectProjectDocuments(
  state: ProjectDocumentStore,
  projectId: string | null | undefined
): Document[] {
  if (!projectId) {
    return EMPTY_PROJECT_DOCUMENTS;
  }
  return state.byProjectId[projectId]?.documents ?? EMPTY_PROJECT_DOCUMENTS;
}

const emptyState = (): ProjectDocumentState => ({
  documents: EMPTY_PROJECT_DOCUMENTS,
  loading: false,
  error: null,
});

function markRealtimeUpsert(
  recentRealtimeUpsertIdsByProject: Record<string, Set<string> | undefined>,
  projectId: string,
  documentId: string
): Record<string, Set<string> | undefined> {
  const existing = recentRealtimeUpsertIdsByProject[projectId] ?? new Set<string>();
  const next = new Set(existing);
  next.add(documentId);
  return {
    ...recentRealtimeUpsertIdsByProject,
    [projectId]: next,
  };
}

/** Keeps in-flight realtime upserts when a stale fetch completes before the API lists the new document. */
function mergeFetchedProjectDocuments(
  local: Document[],
  fetched: Document[],
  recentRealtimeUpsertIds: Set<string> | undefined,
  deletedDocumentIds: Set<string> | undefined
): Document[] {
  const byId = new Map(fetched.map((doc) => [doc.id, doc]));
  for (const doc of local) {
    if (byId.has(doc.id) || deletedDocumentIds?.has(doc.id)) {
      continue;
    }
    if (recentRealtimeUpsertIds?.has(doc.id)) {
      byId.set(doc.id, doc);
    }
  }
  return [...byId.values()];
}

function reconcileRealtimeTracking(
  fetched: Document[],
  recentRealtimeUpsertIds: Set<string> | undefined,
  deletedDocumentIds: Set<string> | undefined
): {
  recentRealtimeUpsertIds?: Set<string>;
  deletedDocumentIds?: Set<string>;
} {
  const fetchedIds = new Set(fetched.map((doc) => doc.id));
  let nextRecent = recentRealtimeUpsertIds;
  if (recentRealtimeUpsertIds) {
    const filtered = new Set(
      [...recentRealtimeUpsertIds].filter((id) => !fetchedIds.has(id))
    );
    nextRecent = filtered.size > 0 ? filtered : undefined;
  }
  let nextDeleted = deletedDocumentIds;
  if (deletedDocumentIds) {
    const filtered = new Set(
      [...deletedDocumentIds].filter((id) => fetchedIds.has(id))
    );
    nextDeleted = filtered.size > 0 ? filtered : undefined;
  }
  return {
    recentRealtimeUpsertIds: nextRecent,
    deletedDocumentIds: nextDeleted,
  };
}

export const useProjectDocumentStore = create<ProjectDocumentStore>((set, get) => ({
  byProjectId: {},
  inflightByProjectId: {},
  recentRealtimeUpsertIdsByProject: {},
  deletedDocumentIdsByProject: {},

  getDocuments: (projectId) => selectProjectDocuments(get(), projectId),

  getLoading: (projectId) => {
    if (!projectId) {
      return false;
    }
    return get().byProjectId[projectId]?.loading ?? false;
  },

  getError: (projectId) => {
    if (!projectId) {
      return null;
    }
    return get().byProjectId[projectId]?.error ?? null;
  },

  fetchProjectDocuments: async (projectId) => {
    const inflight = get().inflightByProjectId[projectId];
    if (inflight) {
      return inflight;
    }

    set((state) => ({
      byProjectId: {
        ...state.byProjectId,
        [projectId]: {
          ...(state.byProjectId[projectId] ?? emptyState()),
          loading: true,
          error: null,
        },
      },
    }));

    const request = documentService
      .getProjectDocuments(projectId)
      .then((documents) => {
        set((state) => {
          const local = state.byProjectId[projectId]?.documents ?? EMPTY_PROJECT_DOCUMENTS;
          const merged = mergeFetchedProjectDocuments(
            local,
            documents,
            state.recentRealtimeUpsertIdsByProject[projectId],
            state.deletedDocumentIdsByProject[projectId]
          );
          const tracking = reconcileRealtimeTracking(
            documents,
            state.recentRealtimeUpsertIdsByProject[projectId],
            state.deletedDocumentIdsByProject[projectId]
          );
          return {
            byProjectId: {
              ...state.byProjectId,
              [projectId]: {
                documents: merged,
                loading: false,
                error: null,
              },
            },
            inflightByProjectId: {
              ...state.inflightByProjectId,
              [projectId]: undefined,
            },
            recentRealtimeUpsertIdsByProject: {
              ...state.recentRealtimeUpsertIdsByProject,
              [projectId]: tracking.recentRealtimeUpsertIds,
            },
            deletedDocumentIdsByProject: {
              ...state.deletedDocumentIdsByProject,
              [projectId]: tracking.deletedDocumentIds,
            },
          };
        });
        return documents;
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Failed to load documents";
        set((state) => ({
          byProjectId: {
            ...state.byProjectId,
            [projectId]: {
              documents: state.byProjectId[projectId]?.documents ?? EMPTY_PROJECT_DOCUMENTS,
              loading: false,
              error: message,
            },
          },
          inflightByProjectId: {
            ...state.inflightByProjectId,
            [projectId]: undefined,
          },
        }));
        throw err;
      });

    set((state) => ({
      inflightByProjectId: {
        ...state.inflightByProjectId,
        [projectId]: request,
      },
    }));

    return request;
  },

  patchDocumentIndexing: (projectId, documentId, indexingStatus, indexingError) => {
    const current = get().byProjectId[projectId];
    if (!current) {
      return false;
    }
    const index = current.documents.findIndex((doc) => doc.id === documentId);
    if (index < 0) {
      return false;
    }
    const existing = current.documents[index];
    if (
      existing.indexingStatus === indexingStatus &&
      (existing.indexingError ?? undefined) === (indexingError ?? undefined)
    ) {
      return true;
    }
    set((state) => {
      const projectState = state.byProjectId[projectId];
      if (!projectState) {
        return state;
      }
      const documents = projectState.documents.map((doc) =>
        doc.id === documentId
          ? {
              ...doc,
              indexingStatus,
              indexingError: indexingError ?? undefined,
            }
          : doc,
      );
      return {
        byProjectId: {
          ...state.byProjectId,
          [projectId]: {
            ...projectState,
            documents,
          },
        },
      };
    });
    return true;
  },

  upsertProjectDocument: (projectId, document) => {
    set((state) => {
      const current = state.byProjectId[projectId] ?? emptyState();
      const index = current.documents.findIndex((doc) => doc.id === document.id);
      const documents =
        index >= 0
          ? current.documents.map((doc, i) => (i === index ? { ...doc, ...document } : doc))
          : [...current.documents, document];
      const deleted = state.deletedDocumentIdsByProject[projectId];
      const nextDeleted = deleted ? new Set(deleted) : new Set<string>();
      nextDeleted.delete(document.id);
      return {
        byProjectId: {
          ...state.byProjectId,
          [projectId]: {
            ...current,
            documents,
          },
        },
        recentRealtimeUpsertIdsByProject: markRealtimeUpsert(
          state.recentRealtimeUpsertIdsByProject,
          projectId,
          document.id
        ),
        deletedDocumentIdsByProject: {
          ...state.deletedDocumentIdsByProject,
          [projectId]: nextDeleted.size > 0 ? nextDeleted : undefined,
        },
      };
    });
  },

  upsertProjectDocumentSummary: (projectId, document) => {
    set((state) => {
      const current = state.byProjectId[projectId] ?? emptyState();
      const index = current.documents.findIndex((item) => item.id === document.id);
      const existing = index >= 0 ? current.documents[index] : undefined;
      if (
        existing &&
        existing.documentName === document.documentName &&
        existing.documentSize === document.documentSize &&
        existing.documentType === document.documentType &&
        existing.indexingStatus === document.indexingStatus
      ) {
        return state;
      }
      const documents =
        index >= 0
          ? current.documents.map((item, itemIndex) =>
              itemIndex === index
                ? {
                    ...item,
                    documentName: document.documentName,
                    documentSize: document.documentSize,
                    documentType: document.documentType,
                    indexingStatus: document.indexingStatus,
                  }
                : item,
            )
          : [
              {
                ...document,
                createdAt: new Date().toISOString(),
                lineageEdges: [],
              },
              ...current.documents,
            ];
      const deleted = state.deletedDocumentIdsByProject[projectId];
      const nextDeleted = deleted ? new Set(deleted) : new Set<string>();
      nextDeleted.delete(document.id);
      return {
        byProjectId: {
          ...state.byProjectId,
          [projectId]: {
            ...current,
            documents,
          },
        },
        recentRealtimeUpsertIdsByProject: markRealtimeUpsert(
          state.recentRealtimeUpsertIdsByProject,
          projectId,
          document.id
        ),
        deletedDocumentIdsByProject: {
          ...state.deletedDocumentIdsByProject,
          [projectId]: nextDeleted.size > 0 ? nextDeleted : undefined,
        },
      };
    });
  },

  removeProjectDocument: (projectId, documentId) => {
    set((state) => {
      const current = state.byProjectId[projectId];
      if (!current) {
        return state;
      }
      const deleted = new Set(state.deletedDocumentIdsByProject[projectId] ?? []);
      deleted.add(documentId);
      const recent = state.recentRealtimeUpsertIdsByProject[projectId];
      const nextRecent = recent ? new Set(recent) : new Set<string>();
      nextRecent.delete(documentId);
      return {
        byProjectId: {
          ...state.byProjectId,
          [projectId]: {
            ...current,
            documents: current.documents.filter((doc) => doc.id !== documentId),
          },
        },
        deletedDocumentIdsByProject: {
          ...state.deletedDocumentIdsByProject,
          [projectId]: deleted,
        },
        recentRealtimeUpsertIdsByProject: {
          ...state.recentRealtimeUpsertIdsByProject,
          [projectId]: nextRecent.size > 0 ? nextRecent : undefined,
        },
      };
    });
  },

  invalidateProject: (projectId) => {
    void get().fetchProjectDocuments(projectId);
  },

  invalidateAllLoadedProjects: async () => {
    const projectIds = Object.keys(get().byProjectId);
    await Promise.all(projectIds.map((projectId) => get().fetchProjectDocuments(projectId)));
  },
}));
